import { useMemo } from 'react'
import { AppTooltip } from '../../../../components/AppTooltip'
import {
  TIMELINE_STATES,
  ALLOWED_TIMELINE_STATES,
  getTimelineStateConfig,
  formatTimelineTooltip,
} from '../constants/activityTimelineConfig'

function formatDurationFromSeconds(seconds) {
  const total = Math.max(Number(seconds || 0), 0)
  const totalMinutes = Math.round(total / 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours}h ${String(minutes).padStart(2, '0')}m`
}

export default function ActivityTimelineCard({
  shiftStartTime,
  shiftEndTime,
  firstLogin,
  totalIdle,
  longestStreak,
  totalBreak,
  shiftTiming,
  timelineSegments = [],
}) {
  const segments = useMemo(
    () => {
      const source = (Array.isArray(timelineSegments) ? timelineSegments : [])
        .filter((segment) => ALLOWED_TIMELINE_STATES.includes(String(segment?.state || '').toUpperCase()))
        .map((segment) => ({
          ...segment,
          state: String(segment?.state || '').toUpperCase(),
          durationSeconds: Number(segment.durationSeconds || 0),
          percent: Number(segment.percent || 0),
        }))

      const totalSeconds = source.reduce((sum, segment) => sum + Math.max(segment.durationSeconds, 0), 0)
      if (totalSeconds <= 0) {
        return source.filter((segment) => segment.percent > 0)
      }
      return source
        .map((segment) => ({
          ...segment,
          percent: Number(((Math.max(segment.durationSeconds, 0) / totalSeconds) * 100).toFixed(2)),
        }))
        .filter((segment) => segment.percent > 0)
    },
    [timelineSegments],
  )

  const displayTotalBreak = useMemo(() => {
    if (totalBreak && String(totalBreak).trim() && totalBreak !== '-') {
      return totalBreak
    }
    const breakSeconds = (Array.isArray(timelineSegments) ? timelineSegments : [])
      .filter((s) => String(s?.state || '').toUpperCase() === 'BREAK')
      .reduce((sum, s) => sum + Math.max(Number(s.durationSeconds || 0), 0), 0)
    return formatDurationFromSeconds(breakSeconds)
  }, [totalBreak, timelineSegments])

  return (
    <article className="rounded-[2rem] bg-surface-container-lowest p-7">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">Activity Timeline</p>
          <h2 className="mt-1 text-[2rem] font-extrabold tracking-tight text-on-surface">Daily Pulse</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {Object.values(TIMELINE_STATES).map((stateItem) => (
            <span
              key={stateItem.key}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${stateItem.badgeClass}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${stateItem.dotClass}`} />
              {stateItem.label}
            </span>
          ))}
        </div>
      </div>

      <div className="mb-7">
        <div className="flex items-center gap-4">
          <span className="w-12 text-xs font-mono text-on-surface-variant">{shiftStartTime || '-'}</span>
          <div className="flex h-10 flex-1 items-stretch gap-1 overflow-hidden rounded-lg">
            {segments.length ? (
              segments.map((segment, index) => {
                const config = getTimelineStateConfig(segment.state)
                const tooltipText = formatTimelineTooltip(segment)
                return (
                  <AppTooltip key={`${segment.state}-${segment.startTime}-${index}`} content={tooltipText}>
                    <div
                      aria-label={tooltipText}
                      className={`h-full min-w-[4px] ${config.barClass} ${index === 0 ? 'rounded-l-md' : ''} ${
                        index === segments.length - 1 ? 'rounded-r-md' : ''
                      }`}
                      style={{ flexBasis: `${segment.percent}%`, flexGrow: segment.percent, flexShrink: 0 }}
                    />
                  </AppTooltip>
                )
              })
            ) : (
              <div className="h-full w-full" />
            )}
          </div>
          <span className="w-12 text-right text-xs font-mono text-on-surface-variant">{shiftEndTime || '-'}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-2xl bg-surface-container-low p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-on-surface-variant">First Login</p>
          <p className="mt-2 text-lg font-black text-primary">{firstLogin || '-'}</p>
        </div>
        <div className="rounded-2xl bg-surface-container-low p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-on-surface-variant">Total Idle</p>
          <p className="mt-2 text-lg font-black text-on-surface">{totalIdle || '0h 00m'}</p>
        </div>
        <div className="rounded-2xl bg-surface-container-low p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-on-surface-variant">Total Break</p>
          <p className="mt-2 text-lg font-black text-on-surface">{displayTotalBreak || '0h 00m'}</p>
        </div>
        <div className="rounded-2xl bg-primary p-4 text-on-primary">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-primary-fixed-dim">Shift Timing</p>
          <p className="mt-2 text-lg font-black">{shiftTiming || '-'}</p>
        </div>
      </div>
    </article>
  )
}
